import { FC, useEffect, useState, useRef, useId } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { AIcon, AIconSize } from '@marriott/mi-web-ui-kit';
import DOMPurify from 'isomorphic-dompurify';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { useUIState, useUIActions } from '../../store';
import { extractUniversalFooterModelFromPageModel } from '../../utils/extractUniversalFooterModel';
import { activatePrivacyHtmlEmbed, usePrivacyEmbedMirrorText } from './privacyCmpOverlay';
import * as Styled from './MConversationalNavGroupVertical.styles';
import {
  MConversationalNavGroupVerticalProps,
  MConversationalNavGroupVerticalModel,
  NavLinkItem,
  FooterNavItem,
  UniversalFooter,
} from './MConversationalNavGroupVertical.types';

const sanitizeFooterHtml = (html: string) => (DOMPurify.sanitize(html, { RETURN_DOM: true }) as HTMLElement).outerHTML;

const normalizeNavItem = (item: NavLinkItem): NavLinkItem => ({
  label: item.label || item.linkLabel || '',
  url: item.url || item.linkURL || '',
  openInNewTab: item.openInNewTab ?? item.openInaNewTab === 'true',
  htmlContent: item.htmlContent,
});

const transformFooterNavItems = (items: FooterNavItem[] = []): NavLinkItem[] => {
  return items
    .map(item => ({
      label: item.linkLabel || '',
      url: item.linkURL || '',
      openInNewTab: item.openInaNewTab === 'true',
      htmlContent: item.htmlContent,
    }))
    .filter(item => item.label || item.htmlContent);
};

type NavLabelVariant = 'mobile' | 'desktop';

const PrivacyMirroredRowLabel: FC<{
  htmlContent: string;
  cmsLabel: string;
  variant: NavLabelVariant;
}> = ({ htmlContent, cmsLabel, variant }) => {
  const Label = variant === 'mobile' ? Styled.MobileNavItemLabel : Styled.NavItemLabel;
  const text = usePrivacyEmbedMirrorText(htmlContent, cmsLabel);
  return <Label>{text}</Label>;
};

const ConversationalNavRowLabel: FC<{
  item: NavLinkItem;
  variant: NavLabelVariant;
  /** CMP placeholders mount in HiddenPrivacyFooterHosts; overlay rows must not duplicate ids. */
  embedHostsRenderedElsewhere?: boolean;
}> = ({ item, variant, embedHostsRenderedElsewhere }) => {
  const Label = variant === 'mobile' ? Styled.MobileNavItemLabel : Styled.NavItemLabel;
  if (item.htmlContent && embedHostsRenderedElsewhere) {
    return <PrivacyMirroredRowLabel htmlContent={item.htmlContent} cmsLabel={item.label} variant={variant} />;
  }
  if (item.htmlContent) {
    const html = sanitizeFooterHtml(item.htmlContent);
    if (item.label) {
      return (
        <Label>
          <span dangerouslySetInnerHTML={{ __html: html }} />
          <span>{item.label}</span>
        </Label>
      );
    }
    return <Label dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return <Label>{item.label}</Label>;
};

const navRowLinkProps = (item: NavLinkItem) => {
  if (item.url) {
    return {
      href: item.url,
      target: item.openInNewTab ? '_blank' : '_self',
      rel: item.openInNewTab ? 'noopener noreferrer' : undefined,
      'aria-label': item.label || undefined,
    } as const;
  }
  return {
    as: 'div' as const,
    'aria-label': item.label || undefined,
  };
};

function overlayFooterRowProps(item: NavLinkItem) {
  if (item.htmlContent && !item.url) {
    return {
      as: 'button' as const,
      type: 'button' as const,
      onClick: () => activatePrivacyHtmlEmbed(item.htmlContent),
    } as const;
  }
  return navRowLinkProps(item);
}

const FOCUSABLE_SUBMENU_SELECTOR = 'a[href], button:not([disabled])';

export const MConversationalNavGroupVerticalComp: FC<MConversationalNavGroupVerticalProps> = props => {
  const { title, navItems, showHeader, universalFooter, onClose: propsOnClose, renderAsOverlay } = props;

  // Read from Zustand store for overlay mode
  const { isSettingsOpen, isMobile } = useUIState();
  const { setIsSettingsOpen } = useUIActions();
  const [showPrivacySubmenu, setShowPrivacySubmenu] = useState(false);
  const settingsPanelRef = useRef<HTMLDivElement>(null);
  const privacySubmenuId = useId();
  const privacyTermsTriggerRef = useRef<HTMLButtonElement>(null);
  const desktopPrivacySubmenuRef = useRef<HTMLDivElement>(null);
  const mobilePrivacySubmenuRef = useRef<HTMLDivElement>(null);
  const focusPrivacySubmenuOnOpenRef = useRef(false);

  const openPrivacySubmenu = (moveFocus: boolean) => {
    focusPrivacySubmenuOnOpenRef.current = moveFocus;
    setShowPrivacySubmenu(true);
  };

  // Always call hooks at the top level, even if not used in all render paths
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageModel = useDCAPageModelStore(state => state.pageModel as any);

  useEffect(() => {
    if (!isSettingsOpen) {
      setShowPrivacySubmenu(false);
    }
  }, [isSettingsOpen]);

  useEffect(() => {
    if (!showPrivacySubmenu || !focusPrivacySubmenuOnOpenRef.current) {
      return;
    }
    focusPrivacySubmenuOnOpenRef.current = false;
    const submenuRoot = isMobile ? mobilePrivacySubmenuRef.current : desktopPrivacySubmenuRef.current;
    submenuRoot?.querySelector<HTMLElement>(FOCUSABLE_SUBMENU_SELECTOR)?.focus();
  }, [showPrivacySubmenu, isMobile]);

  // Click outside handler for desktop view
  useEffect(() => {
    if (!renderAsOverlay || isMobile || !isSettingsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (settingsPanelRef.current && !settingsPanelRef.current.contains(event.target as Node)) {
        setIsSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [renderAsOverlay, isMobile, isSettingsOpen, setIsSettingsOpen]);

  const onClose = renderAsOverlay ? () => setIsSettingsOpen(false) : propsOnClose;

  const WrapperComponent = showHeader ? Styled.Wrapper : Styled.WrapperCompact;

  let finalNavItems = navItems || [];
  let finalTitle = title;
  let settingsNavItems: NavLinkItem[] = [];
  let privacyNavItems: NavLinkItem[] = [];

  if (renderAsOverlay) {
    // Extract footer data from page model store when rendering as overlay
    const universalFooterFromStore = extractUniversalFooterModelFromPageModel(pageModel);

    // Use store data or fallback to prop
    const footerData = universalFooterFromStore || universalFooter;

    settingsNavItems = transformFooterNavItems(footerData?.workwithusItems);
    privacyNavItems = transformFooterNavItems(footerData?.privacyItems);

    finalTitle = 'Settings'; // Always show "Settings" as title
  } else if (universalFooter) {
    // Use universal footer prop when not in overlay mode (standard AEM usage)
    finalNavItems = showHeader
      ? transformFooterNavItems(universalFooter.workwithusItems)
      : transformFooterNavItems(universalFooter.privacyItems);
    finalTitle = universalFooter.resourceProperties?.privacyTitle || title;
  }

  const normalizedItems = finalNavItems.map(normalizeNavItem);

  if (renderAsOverlay) {
    if (!isSettingsOpen) return null;

    // Mobile full-screen drawer view
    if (isMobile) {
      return !showPrivacySubmenu ? (
        <Styled.MobileDrawer>
          <Styled.MobileHeader>
            <Styled.MobileHeaderLeft onClick={onClose} aria-label="Close">
              <AIcon icon="icon-arrow-left" size={AIconSize.L} />
            </Styled.MobileHeaderLeft>
            <Styled.MobileHeaderTitle className="t-subtitle-l">{finalTitle}</Styled.MobileHeaderTitle>
            <Styled.MobileHeaderRight onClick={onClose} aria-label="Close">
              <AIcon icon="icon-clear" size={AIconSize.L} />
            </Styled.MobileHeaderRight>
          </Styled.MobileHeader>
          <Styled.MobileContent>
            <Styled.MobileNavList>
              <Styled.MobileNavItem>
                <Styled.MobileNavItemLabel>Account Settings</Styled.MobileNavItemLabel>
              </Styled.MobileNavItem>
              {privacyNavItems.length > 0 && (
                <Styled.MobileNavItem
                  ref={privacyTermsTriggerRef}
                  as="button"
                  type="button"
                  aria-expanded={showPrivacySubmenu}
                  aria-controls={privacySubmenuId}
                  onClick={() => openPrivacySubmenu(true)}
                >
                  <Styled.MobileNavItemLabel>Privacy & Terms</Styled.MobileNavItemLabel>
                  <Styled.MobileNavItemIcon>
                    <AIcon icon="icon-arrow-right" size={AIconSize.M} />
                  </Styled.MobileNavItemIcon>
                </Styled.MobileNavItem>
              )}
              {settingsNavItems.map((item, index) => (
                <Styled.MobileNavItem key={`nav-item-${index}`} {...overlayFooterRowProps(item)}>
                  <ConversationalNavRowLabel item={item} variant="mobile" embedHostsRenderedElsewhere />
                </Styled.MobileNavItem>
              ))}
            </Styled.MobileNavList>
          </Styled.MobileContent>
        </Styled.MobileDrawer>
      ) : (
        <Styled.MobileDrawer>
          <Styled.MobileHeader>
            <Styled.MobileHeaderLeft
              onClick={() => {
                setShowPrivacySubmenu(false);
                privacyTermsTriggerRef.current?.focus();
              }}
              aria-label="Back"
            >
              <AIcon icon="icon-arrow-left" size={AIconSize.L} />
            </Styled.MobileHeaderLeft>
            <Styled.MobileHeaderTitle className="t-subtitle-l">Privacy & Terms</Styled.MobileHeaderTitle>
            <Styled.MobileHeaderRight onClick={onClose} aria-label="Close">
              <AIcon icon="icon-clear" size={AIconSize.L} />
            </Styled.MobileHeaderRight>
          </Styled.MobileHeader>
          <Styled.MobileContent>
            <Styled.MobileNavList ref={mobilePrivacySubmenuRef} id={privacySubmenuId}>
              {privacyNavItems.map((item, index) => (
                <Styled.MobileNavItem key={`privacy-item-${index}`} {...overlayFooterRowProps(item)}>
                  <ConversationalNavRowLabel item={item} variant="mobile" embedHostsRenderedElsewhere />
                </Styled.MobileNavItem>
              ))}
            </Styled.MobileNavList>
          </Styled.MobileContent>
        </Styled.MobileDrawer>
      );
    }

    // Desktop side-by-side view (existing implementation)
    return (
      <Styled.SettingsOverlay onClick={onClose}>
        <Styled.SettingsPanelContainer ref={settingsPanelRef} onClick={e => e.stopPropagation()}>
          <Styled.Wrapper
            data-component-name="m-conversational-nav-group-vertical"
            data-testid="conversational-nav-group"
          >
            <Styled.Header>
              <Styled.HeaderTitle className="t-subtitle-m">{finalTitle}</Styled.HeaderTitle>
            </Styled.Header>
            <Styled.NavList>
              {privacyNavItems.length > 0 && (
                <Styled.NavItem
                  ref={privacyTermsTriggerRef}
                  as="button"
                  type="button"
                  onMouseEnter={() => openPrivacySubmenu(false)}
                  onClick={() => openPrivacySubmenu(true)}
                  aria-label="Privacy & Terms"
                  aria-expanded={showPrivacySubmenu}
                  aria-controls={privacySubmenuId}
                  $isActive={showPrivacySubmenu}
                >
                  <Styled.NavItemLabel>Privacy & Terms</Styled.NavItemLabel>
                  <Styled.NavItemIcon>
                    <AIcon icon="icon-arrow-right" size={AIconSize.S} aria-hidden="true" />
                  </Styled.NavItemIcon>
                </Styled.NavItem>
              )}
              {settingsNavItems.map((item, index) => (
                <Styled.NavItem
                  key={`nav-item-${index}`}
                  {...overlayFooterRowProps(item)}
                  onMouseEnter={() => setShowPrivacySubmenu(false)}
                >
                  <ConversationalNavRowLabel item={item} variant="desktop" embedHostsRenderedElsewhere />
                </Styled.NavItem>
              ))}
            </Styled.NavList>
          </Styled.Wrapper>

          {showPrivacySubmenu && (
            <Styled.Wrapper
              ref={desktopPrivacySubmenuRef}
              id={privacySubmenuId}
              data-component-name="m-conversational-nav-group-vertical"
              data-testid="conversational-nav-group-privacy"
            >
              <Styled.NavList>
                {privacyNavItems.map((item, index) => (
                  <Styled.NavItem key={`privacy-item-${index}`} {...overlayFooterRowProps(item)}>
                    <ConversationalNavRowLabel item={item} variant="desktop" embedHostsRenderedElsewhere />
                  </Styled.NavItem>
                ))}
              </Styled.NavList>
            </Styled.Wrapper>
          )}
        </Styled.SettingsPanelContainer>
      </Styled.SettingsOverlay>
    );
  }

  return (
    <WrapperComponent data-component-name="m-conversational-nav-group-vertical" data-testid="conversational-nav-group">
      {showHeader && finalTitle && (
        <Styled.Header>
          <Styled.HeaderTitle className="t-subtitle-m">{finalTitle}</Styled.HeaderTitle>
        </Styled.Header>
      )}
      <Styled.NavList>
        {normalizedItems.map((item, index) => (
          <Styled.NavItem key={`nav-item-${index}`} {...navRowLinkProps(item)}>
            <ConversationalNavRowLabel item={item} variant="desktop" />
            {item.url && (
              <Styled.NavItemIcon>
                <AIcon icon="icon-arrow-right" size={AIconSize.S} aria-hidden="true" />
              </Styled.NavItemIcon>
            )}
          </Styled.NavItem>
        ))}
      </Styled.NavList>
    </WrapperComponent>
  );
};

export const MConversationalNavGroupVerticalConfig = {
  emptyLabel: 'MConversationalNavGroupVertical',
  isEmpty: () => true,
  resourceType: `mi-aem-homepage-spa/components/content/universal-footer`,
};

export const MConversationalNavGroupVertical = (props: {
  model?: MConversationalNavGroupVerticalModel;
  cqPath?: string;
  isAuthorMode?: boolean;
}) => {
  const model = props.model || {};

  const universalFooter: UniversalFooter = {
    workwithusItems: model.workwithusItems,
    privacyItems: model.privacyItems,
    resourceProperties: {
      privacyTitle: model.resourceProperties?.privacyTitle,
    },
  };

  const componentProps: MConversationalNavGroupVerticalProps = {
    title: model.resourceProperties?.privacyTitle || model.resourceProperties?.title || model.title,
    navItems: model.navItems,
    showHeader: model.resourceProperties?.showHeader ?? model.showHeader,
    universalFooter,
  };

  const footerContent = <MConversationalNavGroupVerticalComp {...componentProps} />;

  return (
    <div
      id="universal-footer"
      data-component-name="m-conversational-nav-group-vertical"
      data-testid="conversational-nav-group-vertical"
    >
      {props.cqPath?.includes('experiencefragment') ? (
        footerContent
      ) : (
        <EditableComponent config={MConversationalNavGroupVerticalConfig}>{footerContent}</EditableComponent>
      )}
    </div>
  );
};

export default MConversationalNavGroupVertical;
